import { nameKey, slugOf } from "./catalog-import-text.js";
import { supportedCurrencies } from "./money.js";
const err = (code, fieldCode) => ({
    severity: "ERROR",
    code,
    fieldCode,
});
const warn = (code, fieldCode) => ({
    severity: "WARNING",
    code,
    fieldCode,
});
// --- prices --------------------------------------------------------------------------------------------
const FREE = /^(free|complimentary|gratis|no charge|free entry|free of charge)$/i;
const ON_REQUEST = /^(on request|upon request|poa|price on application|quote|quote required|enquire|inquire)$/i;
const UNIT_WORDS = {
    person: "PER_PERSON",
    pax: "PER_PERSON",
    head: "PER_PERSON",
    night: "PER_NIGHT",
    day: "PER_DAY",
    hour: "PER_HOUR",
    group: "PER_GROUP",
    booking: "PER_BOOKING",
    item: "PER_ITEM",
};
/** The unit a price takes when the source names none. The row gets a WARNING (`price_unit`). */
export const DEFAULT_PRICE_UNIT = {
    EVENT: "PER_PERSON",
    DINING: "PER_PERSON",
    EXPERIENCE: "PER_PERSON",
    VILLA: "PER_NIGHT",
    SERVICE: "PER_BOOKING",
};
const SYMBOL_CURRENCY = {
    "₹": "INR",
    "€": "EUR",
    "£": "GBP",
    US$: "USD",
};
const AMOUNT = /^(?:\d{1,3}(?:,\d{3})+|\d{1,2}(?:,\d{2})+,\d{3}|\d+)(?:\.\d{1,2})?$/;
const MAX_INTEGER_DIGITS = 12;
function toMinor(amount) {
    const [whole = "", fraction = ""] = amount.replace(/,/g, "").split(".");
    if (whole.length > MAX_INTEGER_DIGITS)
        return null;
    return Number(whole) * 100 + Number(`${fraction}00`.slice(0, 2));
}
const isSupported = (code) => Object.hasOwn(supportedCurrencies, code);
const NO_PRICE = {
    price: null,
    issues: [],
    currencyRule: null,
    unitDefaulted: false,
};
const noPrice = (...issues) => ({ ...NO_PRICE, issues });
/**
 * A price from its text and the optional currency column. A currency comes ONLY from an ISO code, an
 * unambiguous symbol (`₹`, `Rs`, `€`, `£`, `US$`) or the currency column. A bare `$` or a bare number
 * never sets one: the amounts are kept, `currency` is `null`, and the row gets `AMBIGUOUS_CURRENCY`
 * (staff decide). A price with no unit takes the item type's default unit and a WARNING. `free` is
 * `UNKNOWN` with the terms `Free`; `on request` is `QUOTE_REQUIRED`.
 */
export function normalizePrice(text, currencyCell, itemType) {
    const field = "price_text";
    if (text === null)
        return NO_PRICE;
    if (FREE.test(text)) {
        return {
            ...NO_PRICE,
            price: {
                model: "UNKNOWN",
                currency: null,
                currencyState: "NOT_NEEDED",
                amountMinor: null,
                minAmountMinor: null,
                maxAmountMinor: null,
                unit: null,
                terms: "Free",
            },
        };
    }
    if (ON_REQUEST.test(text)) {
        return {
            ...NO_PRICE,
            price: {
                model: "QUOTE_REQUIRED",
                currency: null,
                currencyState: "NOT_NEEDED",
                amountMinor: null,
                minAmountMinor: null,
                maxAmountMinor: null,
                unit: null,
                terms: null,
            },
        };
    }
    let rest = text;
    let unit = null;
    const unitMatch = /(?:\bper\s+|\/\s*)(person|pax|head|night|day|hour|group|booking|item)\b|\bpp\b/i.exec(rest);
    if (unitMatch !== null) {
        unit =
            unitMatch[1] === undefined ? "PER_PERSON" : (UNIT_WORDS[unitMatch[1].toLowerCase()] ?? null);
        rest = rest.replace(unitMatch[0], " ");
    }
    let from = false;
    if (/\b(onwards|and above|and up|upwards)\b/i.test(rest) || /\+\s*$/.test(rest)) {
        from = true;
        rest = rest.replace(/\b(onwards|and above|and up|upwards)\b/gi, " ").replace(/\+\s*$/, " ");
    }
    if (/^\s*(from|starting\s+(?:at|from)|starts?\s+at)\b/i.test(rest)) {
        from = true;
        rest = rest.replace(/^\s*(from|starting\s+(?:at|from)|starts?\s+at)\b/i, " ");
    }
    rest = rest.replace(/\/-/g, " ").replace(/\b(only|approx\.?|approximately)\b/gi, " ");
    const markers = [...rest.matchAll(/US\$|\$|₹|€|£|\bRs\.?|\b[A-Z]{3}\b/g)].map((match) => match[0]);
    const numbers = [...rest.matchAll(/\d[\d,]*(?:\.\d+)?/g)].map((match) => match[0]);
    const leftover = rest
        .replace(/US\$|\$|₹|€|£|\bRs\.?|\b[A-Z]{3}\b/g, " ")
        .replace(/\d[\d,]*(?:\.\d+)?/g, " ")
        .replace(/\bto\b|[-–—.\s]/gi, "");
    if (leftover !== "" || numbers.length < 1 || numbers.length > 2) {
        return noPrice(err("INVALID_FORMAT", field));
    }
    if (numbers.some((number) => !AMOUNT.test(number)))
        return noPrice(err("INVALID_FORMAT", field));
    const minors = numbers.map(toMinor);
    if (minors.some((minor) => minor === null))
        return noPrice(err("OUT_OF_RANGE", field));
    const [first, second] = minors;
    // The currency: explicit markers first, then the column. `$` is evidence of nothing.
    const explicit = new Set();
    let rule = null;
    for (const marker of markers) {
        if (marker === "$")
            continue;
        const symbol = marker.startsWith("Rs") ? "INR" : SYMBOL_CURRENCY[marker];
        if (symbol !== undefined) {
            explicit.add(symbol);
            rule = "CURRENCY_SYMBOL";
        }
        else if (/^[A-Z]{3}$/.test(marker)) {
            if (!isSupported(marker))
                return noPrice(err("UNSUPPORTED_VALUE", field));
            explicit.add(marker);
            rule = "CURRENCY_CODE";
        }
    }
    if (explicit.size > 1)
        return noPrice(err("PRICING_INCONSISTENT", field));
    let currency = explicit.size === 1 ? [...explicit][0] : null;
    if (currencyCell !== null) {
        const code = currencyCell.toUpperCase();
        if (!/^[A-Z]{3}$/.test(code))
            return noPrice(err("INVALID_FORMAT", "price_currency"));
        if (!isSupported(code))
            return noPrice(err("UNSUPPORTED_VALUE", "price_currency"));
        if (currency !== null && currency !== code)
            return noPrice(err("PRICING_INCONSISTENT", field));
        if (currency === null) {
            currency = code;
            rule = "CURRENCY_COLUMN";
        }
    }
    const issues = [];
    const unitDefaulted = unit === null;
    if (unitDefaulted)
        issues.push(warn("AMBIGUOUS_VALUE", "price_unit"));
    if (currency === null)
        issues.unshift(warn("AMBIGUOUS_CURRENCY", field));
    let model;
    let amountMinor = null;
    let minAmountMinor = null;
    let maxAmountMinor = null;
    if (second !== undefined) {
        if (first > second)
            return noPrice(err("PRICING_INCONSISTENT", field));
        model = "RANGE";
        minAmountMinor = first;
        maxAmountMinor = second;
    }
    else if (from) {
        model = "FROM";
        minAmountMinor = first;
    }
    else {
        model = "FIXED";
        amountMinor = first;
    }
    return {
        price: {
            model,
            currency,
            currencyState: currency === null ? "AMBIGUOUS" : "EXPLICIT",
            amountMinor,
            minAmountMinor,
            maxAmountMinor,
            unit: unit ?? DEFAULT_PRICE_UNIT[itemType],
            terms: null,
        },
        issues,
        currencyRule: rule,
        unitDefaulted,
    };
}
// --- links ----------------------------------------------------------------------------------------------
const TRACKING_PARAMS = /^(utm_[a-z0-9_]+|fbclid|gclid|mc_cid|mc_eid|igshid)$/i;
const MAX_URL = 2048;
/**
 * A link: `www.` gets `https://`, the scheme must be `https` (an `http` link is dropped with a WARNING),
 * the host has a dot, no credentials, at most 2,048 characters. Tracking parameters and the fragment are
 * removed. The link is never fetched.
 */
export function normalizeUrl(text, fieldCode = "external_url") {
    if (text === null)
        return { url: null, issues: [] };
    const candidate = /^www\./i.test(text) ? `https://${text}` : text;
    if (!/^https?:\/\//i.test(candidate))
        return { url: null, issues: [err("INVALID_FORMAT", fieldCode)] };
    if (candidate.length > MAX_URL)
        return { url: null, issues: [err("OUT_OF_RANGE", fieldCode)] };
    let parsed;
    try {
        parsed = new URL(candidate);
    }
    catch {
        return { url: null, issues: [err("INVALID_FORMAT", fieldCode)] };
    }
    if (parsed.username !== "" || parsed.password !== "") {
        return { url: null, issues: [err("INVALID_FORMAT", fieldCode)] };
    }
    const host = parsed.hostname;
    if (!host.includes(".") || host.endsWith(".") || host === "localhost") {
        return { url: null, issues: [err("INVALID_FORMAT", fieldCode)] };
    }
    if (parsed.protocol === "http:")
        return { url: null, issues: [warn("UNSUPPORTED_VALUE", fieldCode)] };
    for (const key of [...parsed.searchParams.keys()]) {
        if (TRACKING_PARAMS.test(key))
            parsed.searchParams.delete(key);
    }
    parsed.hash = "";
    const search = parsed.searchParams.toString();
    const path = parsed.pathname === "/" ? "" : parsed.pathname;
    const url = `${parsed.protocol}//${parsed.host}${path}${search === "" ? "" : `?${search}`}`;
    if (url.length > MAX_URL)
        return { url: null, issues: [err("OUT_OF_RANGE", fieldCode)] };
    return { url, issues: [] };
}
// --- countries ----------------------------------------------------------------------------------------
/** ISO 3166-1 alpha-2 codes (version 1 of the normalizer). */
const ISO_ALPHA2 = new Set(("AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
    "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR " +
    "GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP " +
    "KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT " +
    "MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW " +
    "SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG " +
    "UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW").split(" "));
// Alpha-3 codes and English names for the countries Lafayette serves most. A name or an alpha-3 code
// outside this list is `UNKNOWN_CODE`: staff use the two-letter code.
const COUNTRIES = [
    ["IN", "IND", "India"],
    ["SG", "SGP", "Singapore"],
    ["AE", "ARE", "United Arab Emirates"],
    ["GB", "GBR", "United Kingdom"],
    ["US", "USA", "United States"],
    ["FR", "FRA", "France"],
    ["JP", "JPN", "Japan"],
    ["DE", "DEU", "Germany"],
    ["IT", "ITA", "Italy"],
    ["ES", "ESP", "Spain"],
    ["PT", "PRT", "Portugal"],
    ["CH", "CHE", "Switzerland"],
    ["NL", "NLD", "Netherlands"],
    ["TH", "THA", "Thailand"],
    ["ID", "IDN", "Indonesia"],
    ["MY", "MYS", "Malaysia"],
    ["LK", "LKA", "Sri Lanka"],
    ["MV", "MDV", "Maldives"],
    ["NP", "NPL", "Nepal"],
    ["BT", "BTN", "Bhutan"],
    ["HK", "HKG", "Hong Kong"],
    ["CN", "CHN", "China"],
    ["KR", "KOR", "South Korea"],
    ["AU", "AUS", "Australia"],
    ["NZ", "NZL", "New Zealand"],
    ["CA", "CAN", "Canada"],
    ["MX", "MEX", "Mexico"],
    ["BR", "BRA", "Brazil"],
    ["ZA", "ZAF", "South Africa"],
    ["EG", "EGY", "Egypt"],
    ["MA", "MAR", "Morocco"],
    ["TR", "TUR", "Turkey"],
    ["GR", "GRC", "Greece"],
    ["AT", "AUT", "Austria"],
    ["BE", "BEL", "Belgium"],
    ["IE", "IRL", "Ireland"],
    ["QA", "QAT", "Qatar"],
    ["SA", "SAU", "Saudi Arabia"],
    ["OM", "OMN", "Oman"],
    ["MU", "MUS", "Mauritius"],
    ["SC", "SYC", "Seychelles"],
];
const COUNTRY_BY_KEY = new Map();
for (const [alpha2, alpha3, name] of COUNTRIES) {
    COUNTRY_BY_KEY.set(alpha3.toLowerCase(), alpha2);
    COUNTRY_BY_KEY.set(nameKey(name), alpha2);
}
COUNTRY_BY_KEY.set("uk", "GB");
COUNTRY_BY_KEY.set("uae", "AE");
COUNTRY_BY_KEY.set("unitedstatesofamerica", "US");
COUNTRY_BY_KEY.set("england", "GB");
/** A country as an ISO two-letter code (as itself), an ISO three-letter code or an English name. */
export function normalizeCountry(text) {
    if (text === null)
        return { code: null, issues: [] };
    const upper = text.toUpperCase();
    if (/^[A-Z]{2}$/.test(upper) && ISO_ALPHA2.has(upper))
        return { code: upper, issues: [] };
    const found = COUNTRY_BY_KEY.get(nameKey(text));
    // `uk` is an alias and not an ISO code, so a two-letter key reaches this line only on purpose.
    if (found !== undefined)
        return { code: found, issues: [] };
    return { code: null, issues: [err("UNKNOWN_CODE", "country_code")] };
}
export const MAX_PERSON_NAME_CHARS = 120;
/**
 * Performers from the cleaned cells of the performer columns, in column order. Null cells are dropped.
 * Only when ONE performer column is mapped is a cell split on ` & `, ` and ` and `;` (with several
 * columns each cell is one name: a group such as `Simon & Garfunkel` is not torn apart). A repeat by
 * name key is dropped (the first spelling stays). Positions are 0..n.
 */
export function normalizePeople(cells, splitSingleColumn) {
    const names = [];
    for (const cell of cells) {
        if (cell === null)
            continue;
        if (splitSingleColumn) {
            for (const part of cell.split(/\s+&\s+|\s+and\s+|;/i)) {
                const name = part.trim();
                if (name !== "")
                    names.push(name);
            }
        }
        else {
            names.push(cell);
        }
    }
    const seen = new Set();
    const people = [];
    let tooLong = false;
    for (const name of names) {
        const key = nameKey(name);
        if (key === "" || seen.has(key))
            continue;
        seen.add(key);
        if ([...name].length > MAX_PERSON_NAME_CHARS)
            tooLong = true;
        people.push({ displayName: name, slugKey: slugOf(name), position: people.length });
    }
    return { people, tooLong };
}
/**
 * Finds the code a source word means: the code itself, the label, or a versioned synonym. The
 * comparison ignores case, accents, spaces and punctuation. A word nobody knows is `unknown`.
 */
export function matchTaxonomyWord(word, options, synonyms = {}) {
    const key = nameKey(word);
    if (key === "")
        return { kind: "unknown" };
    for (const option of options) {
        if (nameKey(option.code) === key && option.code.toLowerCase() === word.toLowerCase()) {
            return { kind: "match", code: option.code, rule: "CODE_MATCH" };
        }
    }
    for (const option of options) {
        if (nameKey(option.code) === key)
            return { kind: "match", code: option.code, rule: "CODE_MATCH" };
    }
    for (const option of options) {
        if (nameKey(option.label) === key)
            return { kind: "match", code: option.code, rule: "LABEL_MATCH" };
    }
    const synonym = synonyms[key];
    if (synonym !== undefined && options.some((option) => option.code === synonym)) {
        return { kind: "match", code: synonym, rule: "SYNONYM" };
    }
    return { kind: "unknown" };
}
/** Versioned synonyms of the enumerated fields (keys are `nameKey` forms). */
export const TAXONOMY_SYNONYMS_V1 = {
    dress_code: {
        smartcasual: "SMART_CASUAL",
        blacktie: "BLACK_TIE",
        jacketrequired: "SMART",
        elegant: "SMART",
    },
    reservation_policy: {
        mandatory: "REQUIRED",
        bookingrequired: "REQUIRED",
        reservationrequired: "REQUIRED",
        recommended: "RECOMMENDED",
        notneeded: "NOT_REQUIRED",
        notrequired: "NOT_REQUIRED",
        walkin: "WALK_IN_ONLY",
        walkins: "WALK_IN_ONLY",
        walkinonly: "WALK_IN_ONLY",
    },
};
